import type { Playlist } from './musicTypes';

/**
 * Плейлисты — чистые функции над списком: провайдер только хранит результат.
 *
 * <p>Плейлист держит ссылки на треки фонотеки, а не их копии: один файл может стоять в пяти
 * плейлистах и занимать место один раз. Поэтому «убрать из плейлиста» не трогает файл, а
 * «удалить с устройства» убирает трек отовсюду — висячая ссылка в плейлисте показала бы
 * строку, которая ничего не играет.
 */

export const PLAYLIST_NAME_MAX = 60;

export function cleanPlaylistName(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, PLAYLIST_NAME_MAX);
}

export function createPlaylist(
  list: readonly Playlist[],
  name: string,
  trackIds: readonly string[] = [],
  id: string = `pl${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
): { list: Playlist[]; id: string } {
  const playlist: Playlist = { id, name: cleanPlaylistName(name), trackIds: [...new Set(trackIds)], createdAt: Date.now() };
  return { list: [...list, playlist], id };
}

export function renamePlaylist(list: readonly Playlist[], id: string, name: string): Playlist[] {
  const clean = cleanPlaylistName(name);
  return clean ? list.map((p) => (p.id === id ? { ...p, name: clean } : p)) : [...list];
}

export function deletePlaylist(list: readonly Playlist[], id: string): Playlist[] {
  return list.filter((p) => p.id !== id);
}

/** Повторно трек не добавляется: два одинаковых трека подряд в плейлисте — почти всегда промах. */
export function addToPlaylist(list: readonly Playlist[], id: string, trackIds: readonly string[]): Playlist[] {
  return list.map((p) => {
    if (p.id !== id) {
      return p;
    }
    const have = new Set(p.trackIds);
    return { ...p, trackIds: [...p.trackIds, ...trackIds.filter((t) => !have.has(t))] };
  });
}

export function removeFromPlaylist(list: readonly Playlist[], id: string, trackId: string): Playlist[] {
  return list.map((p) => (p.id === id ? { ...p, trackIds: p.trackIds.filter((t) => t !== trackId) } : p));
}

/** Трек удалён с устройства — убрать его изо всех плейлистов. */
export function forgetTrack(list: readonly Playlist[], trackId: string): Playlist[] {
  return list.map((p) => (p.trackIds.includes(trackId) ? { ...p, trackIds: p.trackIds.filter((t) => t !== trackId) } : p));
}

/** Восстановить из хранилища: битые записи отбрасываются, ссылки на исчезнувшие треки — тоже. */
export function normalizePlaylists(raw: unknown, knownTrackIds: ReadonlySet<string>): Playlist[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((p): p is Playlist => Boolean(p) && typeof p.id === 'string' && typeof p.name === 'string' && Array.isArray(p.trackIds))
    .map((p) => ({
      id: p.id,
      name: cleanPlaylistName(p.name) || '—',
      trackIds: p.trackIds.filter((t) => typeof t === 'string' && knownTrackIds.has(t)),
      createdAt: typeof p.createdAt === 'number' ? p.createdAt : 0,
    }));
}

/** Имя по умолчанию для нового плейлиста: «Плейлист 3», не повторяя существующие. */
export function defaultPlaylistName(list: readonly Playlist[], base: string): string {
  const names = new Set(list.map((p) => p.name));
  for (let n = list.length + 1; ; n += 1) {
    const candidate = `${base} ${n}`;
    if (!names.has(candidate)) {
      return candidate;
    }
  }
}
